
import React, { useState, useEffect } from "react";
import { getFileUrl, getFileMetadata } from "@/services/fileStorageService";
import { toast } from "@/hooks/use-toast";
import PDFViewer from "@/components/course/PDFViewer";
import VideoPlayer from "@/components/course/VideoPlayer";
import { AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ContentViewerProps {
  contentType: string;
  filePath?: string | null;
  url?: string | null;
  title: string;
}

const ContentViewer: React.FC<ContentViewerProps> = ({
  contentType,
  filePath,
  url,
  title,
}) => {
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Handle content based on type
        if (contentType === 'pdf' && filePath) {
          const url = await getFileUrl('course-content', filePath);
          setContentUrl(url);
        } else if (contentType === 'video') {
          // Handle both uploaded videos and external URLs
          if (filePath) {
            const url = await getFileUrl('course-content', filePath);
            setContentUrl(url);
          } else if (url) {
            setContentUrl(url);
          } else {
            throw new Error("No video source provided");
          }
        } else if (contentType === 'link' && url) {
          setContentUrl(url);
        } else if (contentType === 'audio' && filePath) {
          const url = await getFileUrl('course-content', filePath);
          setContentUrl(url);
        } else if (filePath) {
          // Generic file handler for any other content with a file path
          const url = await getFileUrl('course-content', filePath);
          setContentUrl(url);
        } else if (url) {
          // Use provided URL as fallback
          setContentUrl(url);
        } else {
          throw new Error("No content source provided");
        }
      } catch (err) {
        console.error("Error loading content:", err);
        setError("Failed to load content. Please try again later.");
        toast({
          title: "Error",
          description: "Could not load the content.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadContent();
  }, [contentType, filePath, url]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[400px] bg-gray-100 rounded-md">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4">Loading content...</p>
        </div>
      </div>
    );
  }
  
  if (error || !contentUrl) {
    return (
      <div className="flex justify-center items-center h-[400px] bg-gray-100 rounded-md">
        <div className="text-center max-w-md p-6">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h3 className="text-lg font-medium mb-2">Content Not Available</h3>
          <p>{error || "Content could not be loaded."}</p>
        </div>
      </div>
    );
  }
  
  // Render content based on type
  const renderContent = () => {
    switch (contentType) {
      case 'pdf':
        return <PDFViewer filePath={filePath} title={title} />;
        
      case 'video':
        return (
          <div className="w-full aspect-video rounded-md overflow-hidden shadow-md">
            <VideoPlayer 
              src={contentUrl} 
              onTimeUpdate={(currentTime) => {
                // Time tracking could be implemented here
              }}
              className="w-full h-full"
            />
          </div>
        );
        
      case 'audio':
        return (
          <div className="w-full bg-gray-100 rounded-md p-4">
            <h3 className="font-medium mb-3">{title}</h3>
            <audio controls className="w-full">
              <source src={contentUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        );
        
      case 'link':
        return (
          <div className="w-full bg-gray-100 rounded-md p-6 text-center">
            <h3 className="font-medium mb-4">{title}</h3>
            <p className="text-gray-600 mb-4">This content is available at an external link.</p>
            <Button 
              onClick={() => window.open(contentUrl, '_blank')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open External Resource
            </Button>
          </div>
        );
        
      default:
        return (
          <div className="w-full bg-gray-100 rounded-md p-6 text-center">
            <h3 className="font-medium mb-4">{title}</h3>
            <p className="text-gray-600 mb-4">This content is available for download.</p>
            <Button 
              onClick={() => window.open(contentUrl, '_blank')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View or Download Content
            </Button>
          </div>
        );
    }
  };
  
  return (
    <div className="w-full">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      {renderContent()}
    </div>
  );
};

export default ContentViewer;
